import { FC, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Switch, Table } from 'antd'
import PerText from '../../../components/custom_components/Text'
import { KudosLegalEntityVisibilityDto, KudosLegalEntityVisibilityFilterInput } from '../../../types'
import { ApolloError } from '@apollo/client'
import classNames from 'classnames'
import { PerEmpty } from '../../../components/custom_components/Empty'
import { NewLoadingAndErrors } from '../../../components/custom_components/LoadingAndErrors/NewLoadingAndErrors'
import BackgroundBox from '../../../components/custom_components/BackgroundBox'
import EditKudosLegalEntityVisibilityModal from '../../../components/custom_components/KudosVisibilityModal/EditKudosLegalEntityVisibilityModal'
import { isHRAdminOfThisCountry } from '../../user/util'
import { useAuth } from '../../../context/auth-context'

interface KudosLegalEntityVisibilityTableProps {
  kudosLegalEntityVisibilityList?: KudosLegalEntityVisibilityDto[]
  loading: boolean
  isLoadingMore: boolean
  error?: ApolloError
  filters: KudosLegalEntityVisibilityFilterInput
  showMoreButtonVisible: boolean
  resetFiltersAfterVisibilityChange: () => void
}

export const KudosLegalEntityVisibilityTable: FC<KudosLegalEntityVisibilityTableProps> = ({
  kudosLegalEntityVisibilityList,
  loading,
  isLoadingMore,
  error,
  filters,
  showMoreButtonVisible,
  resetFiltersAfterVisibilityChange,
}) => {
  const { t } = useTranslation()
  const { user } = useAuth()
  const [editModalVisible, setEditModalVisible] = useState(false)
  const [kudosLegalEntityVisibilityToEdit, setKudosLegalEntityVisibilityToEdit] = useState<KudosLegalEntityVisibilityDto | null>(null)

  if ((loading && !isLoadingMore) || error || !kudosLegalEntityVisibilityList) {
    return (
      <BackgroundBox r height={'withTabsAndInfoBox'}>
        <NewLoadingAndErrors
          isLoading={loading && !isLoadingMore}
          error={error}
          hasNoData={!kudosLegalEntityVisibilityList}
          showSpinner
          t={t}
        />
      </BackgroundBox>
    )
  }

  const handleEditClick = (kudosLegalEntityVisibility: KudosLegalEntityVisibilityDto) => {
    setKudosLegalEntityVisibilityToEdit(kudosLegalEntityVisibility)
    setEditModalVisible(true)
  }

  const handleEditClose = () => {
    setEditModalVisible(false)
    setKudosLegalEntityVisibilityToEdit(null)
  }

  const columns = [
    {
      title: t('kudos.table.legalEntityName'),
      key: 'legalEntityName',
      width: '40%',
      render: (record: KudosLegalEntityVisibilityDto) => <PerText fontSize="m">{record.legalEntityName}</PerText>,
    },
    {
      title: t('kudos.table.country'),
      key: 'country',
      width: '20%',
      render: (record: KudosLegalEntityVisibilityDto) => <PerText fontSize="m">{record.country}</PerText>,
    },
    {
      title: t('kudos.table.visibility'),
      key: 'kudosEnabled',
      width: '40%',
      render: (record: KudosLegalEntityVisibilityDto) => (
        <Switch
          style={!isHRAdminOfThisCountry(record.country, !!user.roles ? user.roles : []) ? { cursor: 'not-allowed' } : undefined}
          checked={record.kudosEnabled}
          disabled={!isHRAdminOfThisCountry(record.country, !!user.roles ? user.roles : [])}
          onChange={() => {
            handleEditClick(record)
          }}
        />
      ),
    },
  ]

  return (
    <>
      <Table
        className={classNames('light small scroll-compensation', {
          viewWithNoSortingAndTabsAndInfoBoxAndShowMoreButton: showMoreButtonVisible,
          viewWithNoSortingAndTabsAndInfoBox: !showMoreButtonVisible,
        })}
        columns={columns}
        dataSource={kudosLegalEntityVisibilityList.map((kudos) => ({ ...kudos, key: kudos.id }))}
        pagination={false}
        rowKey="id"
        rowClassName={() => 'minHeight-row'}
        scroll={{ y: true }}
        locale={{
          emptyText: (
            <PerEmpty
              heading={t('kudos.table.noLegalEntityVisibilityWithFilters')}
              info={t('kudos.table.noLegalEntityVisibilityWithFiltersInfo')}
              icon
            />
          ),
        }}
      />

      <EditKudosLegalEntityVisibilityModal
        visible={editModalVisible}
        onClose={handleEditClose}
        kudosLegalEntityVisibility={kudosLegalEntityVisibilityToEdit}
        filters={filters}
        resetFiltersAfterVisibilityChange={resetFiltersAfterVisibilityChange}
      />
    </>
  )
}
